<template>
    <div class="flex flex-col gap-2 mt-7 border p-4 rounded-lg">
        <!-- Hidden file input for uploads -->
        <input
            ref="fileInput"
            type="file"
            style="display: none"
            @change="handleFileUpload"
            accept=".pdf,.doc,.docx,.xlsx,.csv,.ppt,.png,.jpg,.jpeg"
        />

        <ValidationObserver v-slot="{ handleSubmit }">
            <div class="flex mt-2">
                <span class="text-sm font-semibold flex flex-grow">{{ certificateTitle }}</span>
                <span class="flex gap-1 cursor-pointer" v-if="isEdit" @click="handleSubmit(addinfo)">
                <icon icon-name="icon-plus" class-name="text-carrotOrange" size="xms" />
                <span class="text-sm text-flame font-semibold">Add another</span>
                </span>
            </div>
            <divider />
            <div class="grid text-darkPurple" style="margin-top: 1.125rem"
                v-for="(data, index) in certificateInfo" :key="index"
            >
                <div class="grid grid-cols-12 gap-6" v-if="isEdit">
                    <div class="col-span-4 lg:col-span-3" v-for="(field, fieldKey) in fieldConfig" :key="fieldKey">
                        <c-text
                            v-if="field.type === 'text'"
                            placeholder="--Enter--"
                            variant="w-full h-10"
                            :label="field.label"
                            :rules="field.required ? ['required'] : []"
                            v-model="data[field.key]" />
                        <div v-else-if="field.type === 'date'">
                            <label class="date-label">{{ field.label }}</label>
                            <div class="date-flex">
                                <datepicker
                                    placeholder="--Enter--"
                                    input-class="date-input"
                                    style="width: 100%; outline: none"
                                    v-model="data[field.key]"
                                    v-bind="field.yearOnly ? { 'minimum-view': 'year', 'maximum-view': 'year' } : {}"
                                    :format="field.yearOnly ? 'yyyy' : 'MMM dd, yyyy'"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="col-span-4 lg:col-span-3 flex items-end">
                        <div  class="w-full flex flex-wrap gap-4">
                            <div v-if="data.filePath" class="flex justify-center items-center gap-2 text-sm font-semibold cursor-pointer" @click="downloadFile(data.filePath)">
                                <div class="w-5 h-5">
                                    <icon icon-name="pdf" size="xs" class="icon"/>
                                </div>
                                <div class="flex flex-col justify-center gap-1 text-xs">
                                    <p class="text-flame font-semibold">{{ getFileName(data.filePath)  }}</p>
                                </div>
                            </div>

                            <div class="flex justify-center items-center" v-if="isEdit && data.filePath">
                                <span class="flex flex-col cursor-pointer justify-center my-4 text-flame"
                                    @click="removeFile(index)">
                                    Remove
                                </span>
                            </div>

                            <div v-if="!data.filePath" class="flex justify-center items-center gap-1 text-base font-normal text-dynamicBackBtn cursor-pointer" @click="triggerFileUpload(index, data)">
                                <div class="w-5 h-5 my-4">
                                    <icon icon-name="red-upload-icon" size="xs" class="icon"/>
                                </div>
                                Upload
                            </div>

                            <div class="flex justify-center items-center" v-if="isEdit && index > 0">
                                <span
                                    class="flex flex-col cursor-pointer justify-center ml-4"
                                    @click="deleteEducationalInfo(index, data)"
                                >
                                    <Icon icon-name="icon-trash" size="xs" class-name="text-desire" />
                                </span>
                            </div>
                        </div>
                    </div>

                </div>

                <div class="grid grid-cols-12 gap-6" v-else>
                    <div class="col-span-4 lg:col-span-3" v-for="(field, fieldKey) in fieldConfig" :key="fieldKey">
                        <template v-if="field.type === 'text'">
                            <label class="date-label">{{ field.label }}</label>
                            <div class="view-field">{{ data[field.key] || '-' }}</div>
                        </template>
                        <div v-else-if="field.type === 'date'">
                            <label class="date-label">{{ field.label }}</label>
                            <div class="date-flex_disabled">
                                <datepicker
                                    placeholder="--Enter--"
                                    input-class="date-input date-input_disabled"
                                    style="width: 100%; outline: none"
                                    v-model="data[field.key]"
                                    :disabled="!isEdit"
                                    :minimum-view="field.yearOnly ? 'year' : 'month'"
                                    :maximum-view="field.yearOnly ? 'year' : 'month'"
                                    :format="field.yearOnly ? 'yyyy' : 'MMM dd, yyyy'"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="col-span-4 lg:col-span-3 flex items-end gap-1 text-sm font-semibold">
                        <div class=" w-5 h-5">
                            <icon v-if="data.filePath" icon-name="pdf" size="xs" class="icon"/>
                            <icon v-else icon-name="red-upload-icon" size="xs" class="icon"/>
                        </div>
                        <div v-if="data.filePath" class="cursor-pointer text-flame" @click="downloadFile(data.filePath)">
                            {{ getFileName(data.filePath)  }}
                        </div>
                        <div v-else class="text-flame">
                            {{ getFileName(data.filePath)  }}
                        </div>
                    </div>
                </div>
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
import { ValidationObserver, extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import Datepicker from "vuejs-datepicker";
import Divider from "@/components/divider";
import axios from "axios";


extend("required", required);

export default {
    name: "Certificate",
    components: {
        ValidationObserver,
        CText,
        Datepicker,
        Divider,
    },
    props: {
        certificateTitle: {
            type: String,
            default: ''
        },
        isEdit: {
            type: Boolean,
            default: false
        },
        isUploaded: {
            type: Boolean,
            default: false
        },
        certificateInfo: {
            type: Array,
            default: () => []
        },
        certificateType: {
            type: String,
            default: 'educational'
        }
    },
    data() {
        return {
            currentUploadIndex: null,
            currentUploadData: null,
        };
    },
    computed: {
        fieldConfig() {
            const configs = {
                educational: {
                    field1: { key: 'qualification', label: 'Qualification', required: true, type: 'text' },
                    field2: { key: 'courseOfStudy', label: 'Course of Study', required: true, type: 'text' },
                    field3: { key: 'institution', label: 'Institution', required: true, type: 'text' },
                    field4: { key: 'yearOfAward', label: 'Year Of Award', required: true, type: 'date', yearOnly: true }
                },
                certification: {
                    field1: { key: 'qualification', label: 'Qualification', required: true, type: 'text' },
                    field2: { key: 'institution', label: 'Institution', required: true, type: 'text' },
                    field3: { key: 'yearOfAward', label: 'Year Of Award', required: true, type: 'date', yearOnly: true },
                    field4: { key: 'dateOfExpiration', label: 'Date Of Expiration', required: false, showIf: true, type: 'date', yearOnly: false }
                },
                membership: {
                    field1: { key: 'qualification', label: 'Qualification', required: true, type: 'text' },
                    field2: { key: 'institution', label: 'Institution', required: true, type: 'text' },
                    field3: { key: 'dateOfMembership', label: 'Date of Membership', required: true, type: 'date', yearOnly: false },
                    field4: { key: 'dateOfExpiration', label: 'Date Of Expiration', required: false, showIf: true, type: 'date', yearOnly: false }
                },
                otherTraining: {
                    field1: { key: 'qualification', label: 'Qualification', required: true, type: 'text' },
                    field2: { key: 'institution', label: 'Institution', required: true, type: 'text' },
                    field3: { key: 'yearOfAward', label: 'Date of Training', required: true, type: 'date', yearOnly: false }
                }
            };

            return configs[this.certificateType] || configs.educational;
        }
    },
    methods: {
        addinfo(){
            this.$emit('add-info');
        },
        deleteEducationalInfo(index, data){
            this.$emit('delete-educational-info', index, data);
        },
        triggerFileUpload(index, data) {
            this.currentUploadIndex = index;
            this.currentUploadData = data;
            this.$refs.fileInput.click();
        },
        async handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Validate file type
            const allowedTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv',
                'application/vnd.ms-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'image/png',
                'image/jpeg',
                'image/jpg'
            ];

            if (!allowedTypes.includes(file.type)) {
                this.$toasted.error('Unsupported file type');
                return;
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                this.$toasted.error('File size must be less than 5MB');
                return;
            }

            try {
                this.$toasted.info('Uploading file...', { duration: 3000 });

                const formData = new FormData();
                formData.append('file', file);

                const url = process.env.VUE_APP_IMAGE_UPLOAD;
                const { data } = await axios.post(url, formData);

                if (this.currentUploadData && this.currentUploadIndex !== undefined) {
                    this.currentUploadData.filePath = data.fileUrl;
                }

                this.$emit('update-file-path', this.currentUploadIndex, data.fileUrl);
                this.$toasted.success('File uploaded successfully', { duration: 3000 });

            } catch (error) {
                this.$toasted.error('File upload failed', { duration: 3000 });
            }

            // Reset the input
            const input = event.target;
            input.value = '';
        },

        getFileName(url) {
            if (!url) return ' Upload';
            const parts = url.split('/');
            const fileName = parts[parts.length - 1] || 'uploaded file';

            // Truncate filename if it's too long (max 30 characters)
            if (fileName.length > 30) {
                const extension = fileName.split('.').pop();
                const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
                const truncatedName = nameWithoutExt.substring(0, 30);
                return `${truncatedName}...${extension}`;
            }

            return fileName;
        },
        downloadFile(fileUrl) {
            if (!fileUrl) return;

            try {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.download = this.getFileName(fileUrl);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                this.$toasted.error('Failed to download file', { duration: 3000 });
            }
        },
        removeFile(index) {
            this.$emit('update-file-path', index, null);
        }
    },
};
</script>
<style scoped>
    .icon{
        width: 100% !important;
        height: 100% !important;
    }
    .date-flex{
        overflow: visible;
        position: relative;
    }
    :deep(.vdp-datepicker){
        width: 100%;
    }
    :deep(.vdp-datepicker__calendar){
        right: 0 !important;
        left: auto !important;
        max-width: 95vw;
        overflow-x: hidden;
        z-index: 9999;
    }
    @media (max-width: 640px){
        :deep(.vdp-datepicker__calendar){
            right: 8px !important;
            max-width: calc(100vw - 16px);
        }
    }
    .view-field{
        border: 1px solid #EEEEEE;
        background: #EEEEEE;
        border-radius: 5px;
        padding: 10px 15px;
        color: #565456;
        min-height: 40px;
        white-space: pre-wrap;
        word-break: break-word;
    }
</style>